import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import {
    approveHRLeaveRequest,
    createHRAttendance,
    createHRApplicant,
    createHRComplianceDocument,
    createHRDepartment,
    createHREmployee,
    createHRJobOpening,
    createHRLeaveRequest,
    createHRLeaveType,
    createHRPayrollItem,
    createHRPayrollPeriod,
    createHRPerformanceReview,
    createHRRole,
    createHRSalaryStructure,
    fetchHRAttendance,
    fetchHRApplicants,
    fetchHRComplianceDocuments,
    fetchHRDashboard,
    fetchHRDepartments,
    fetchHREmployees,
    fetchHRJobOpenings,
    fetchHRLeaveRequests,
    fetchHRLeaveTypes,
    fetchHRPayrollItems,
    fetchHRPayrollPeriods,
    fetchHRPerformanceReviews,
    fetchHRRoles,
    fetchHRSalaryStructures,
    fetchStores,
    rejectHRLeaveRequest,
} from "../api/api";

const modules = [
    { key: "employees", label: "Employee Master" },
    { key: "attendance", label: "Attendance" },
    { key: "leave", label: "Leave" },
    { key: "payroll", label: "Payroll" },
    { key: "performance", label: "Performance" },
    { key: "compliance", label: "Compliance" },
    { key: "recruitment", label: "Recruitment" },
    { key: "analytics", label: "Analytics" },
    { key: "settings", label: "Settings" },
];

const blankEmployee = {
    employee_number: "",
    first_name: "",
    last_name: "",
    id_number: "",
    kra_pin: "",
    sha_number: "",
    nssf_number: "",
    phone_number: "",
    email: "",
    employment_type: "FULL_TIME",
    store: "",
    department: "",
    role: "",
    salary_structure: "",
    supervisor: "",
    status: "ACTIVE",
    hire_date: "",
};

const currency = (value) => `Ksh. ${Math.round(Number(value || 0)).toLocaleString()}`;
const percent = (value) => Math.min(100, Math.max(0, Number(value || 0)));

const HR = () => {
    const [activeModule, setActiveModule] = useState("employees");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const [dashboard, setDashboard] = useState({});
    const [stores, setStores] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [salaryStructures, setSalaryStructures] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [payrollPeriods, setPayrollPeriods] = useState([]);
    const [payrollItems, setPayrollItems] = useState([]);
    const [performanceReviews, setPerformanceReviews] = useState([]);
    const [complianceDocuments, setComplianceDocuments] = useState([]);
    const [jobOpenings, setJobOpenings] = useState([]);
    const [applicants, setApplicants] = useState([]);

    const [employeeForm, setEmployeeForm] = useState(blankEmployee);
    const [departmentName, setDepartmentName] = useState("");
    const [roleForm, setRoleForm] = useState({ department: "", title: "" });
    const [salaryForm, setSalaryForm] = useState({
        name: "",
        employment_type: "FULL_TIME",
        pay_frequency: "MONTHLY",
        basic_salary: "0",
        daily_rate: "0",
        housing_allowance: "0",
        transport_allowance: "0",
        other_allowances: "0",
        overtime_hourly_rate: "0",
    });
    const [attendanceForm, setAttendanceForm] = useState({
        employee: "",
        store: "",
        attendance_date: "",
        shift_name: "",
        late_minutes: "0",
        overtime_hours: "0",
        is_absent: false,
    });
    const [leaveForm, setLeaveForm] = useState({
        employee: "",
        leave_type: "",
        start_date: "",
        end_date: "",
        days_requested: "",
        balance_before: "0",
        balance_after: "0",
        reason: "",
    });
    const [leaveTypeForm, setLeaveTypeForm] = useState({
        name: "",
        annual_entitlement_days: "21",
        statutory_minimum_days: "21",
        is_paid: true,
    });
    const [payrollPeriodForm, setPayrollPeriodForm] = useState({ name: "", start_date: "", end_date: "" });
    const [payrollItemForm, setPayrollItemForm] = useState({
        period: "",
        employee: "",
        store: "",
        basic_pay: "0",
        allowances: "0",
        overtime_pay: "0",
        deductions: "0",
        paye: "0",
        sha: "0",
        nssf: "0",
        net_pay: "0",
    });

    const [performanceForm, setPerformanceForm] = useState({
        employee: "",
        period_start: "",
        period_end: "",
        attendance_score: "0",
        late_days: "0",
        stock_variance_flags: "0",
        return_void_flags: "0",
        action: "",
        notes: "",
    });
    const [complianceForm, setComplianceForm] = useState({
        employee: "",
        document_type: "SHA",
        document_number: "",
        expiry_date: "",
        status: "VALID",
        notes: "",
    });
    const [jobOpeningForm, setJobOpeningForm] = useState({
        title: "",
        store: "",
        department: "",
        role: "",
        status: "OPEN",
        opened_at: "",
    });
    const [applicantForm, setApplicantForm] = useState({
        job_opening: "",
        full_name: "",
        phone_number: "",
        email: "",
        status: "APPLIED",
        notes: "",
    });

    const loadHR = useCallback(async () => {
        setLoading(true);
        try {
            const [
                dashboardData,
                storeData,
                departmentData,
                roleData,
                salaryData,
                employeeData,
                attendanceData,
                leaveTypeData,
                leaveRequestData,
                payrollPeriodData,
                payrollItemData,
                performanceData,
                complianceData,
                jobOpeningData,
                applicantData,
            ] = await Promise.all([
                fetchHRDashboard(),
                fetchStores(),
                fetchHRDepartments(),
                fetchHRRoles(),
                fetchHRSalaryStructures(),
                fetchHREmployees(),
                fetchHRAttendance(),
                fetchHRLeaveTypes(),
                fetchHRLeaveRequests(),
                fetchHRPayrollPeriods(),
                fetchHRPayrollItems(),
                fetchHRPerformanceReviews(),
                fetchHRComplianceDocuments(),
                fetchHRJobOpenings(),
                fetchHRApplicants(),
            ]);

            setDashboard(dashboardData || {});
            setStores(Array.isArray(storeData) ? storeData : (storeData?.results || []));
            setDepartments(departmentData);
            setRoles(roleData);
            setSalaryStructures(salaryData);
            setEmployees(employeeData);
            setAttendance(attendanceData);
            setLeaveTypes(leaveTypeData);
            setLeaveRequests(leaveRequestData);
            setPayrollPeriods(payrollPeriodData);
            setPayrollItems(payrollItemData);
            setPerformanceReviews(performanceData);
            setComplianceDocuments(complianceData);
            setJobOpenings(jobOpeningData);
            setApplicants(applicantData);
        } catch (error) {
            console.error("Failed to load HR data:", error);
            setMessage({ type: "danger", text: "Failed to load HR data." });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHR();
    }, [loadHR]);

    const submit = async (handler, successText) => {
        setSaving(true);
        setMessage(null);
        try {
            await handler();
            await loadHR();
            setMessage({ type: "success", text: successText });
        } catch (error) {
            console.error("HR save failed:", error);
            const detail = error.response?.data?.detail || JSON.stringify(error.response?.data || {});
            setMessage({ type: "danger", text: detail && detail !== "{}" ? detail : "Could not save HR record." });
        } finally {
            setSaving(false);
        }
    };

    const selectedEmployee = useMemo(
        () => employees.find((employee) => String(employee.id) === String(payrollItemForm.employee)),
        [employees, payrollItemForm.employee]
    );

    useEffect(() => {
        if (!selectedEmployee) return;
        setPayrollItemForm((current) => ({
            ...current,
            store: current.store || selectedEmployee.store,
        }));
    }, [selectedEmployee]);

    const renderSelect = (value, onChange, options, labelKey = "name") => (
        <Form.Select value={value} onChange={(event) => onChange(event.target.value)}>
            <option value="">Select</option>
            {options.map((option) => (
                <option key={option.id} value={option.id}>
                    {option[labelKey]}
                </option>
            ))}
        </Form.Select>
    );

    const renderEmployees = () => (
        <>
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Add Employee</Card.Title>
                    <Row className="g-2">
                        <Col md={2}><Form.Control placeholder="Emp No." value={employeeForm.employee_number} onChange={(e) => setEmployeeForm({ ...employeeForm, employee_number: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="First name" value={employeeForm.first_name} onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="Last name" value={employeeForm.last_name} onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="ID number" value={employeeForm.id_number} onChange={(e) => setEmployeeForm({ ...employeeForm, id_number: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="KRA PIN" value={employeeForm.kra_pin} onChange={(e) => setEmployeeForm({ ...employeeForm, kra_pin: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="SHA" value={employeeForm.sha_number} onChange={(e) => setEmployeeForm({ ...employeeForm, sha_number: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="NSSF" value={employeeForm.nssf_number} onChange={(e) => setEmployeeForm({ ...employeeForm, nssf_number: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="Phone" value={employeeForm.phone_number} onChange={(e) => setEmployeeForm({ ...employeeForm, phone_number: e.target.value })} /></Col>
                        <Col md={2}><Form.Control type="email" placeholder="Email" value={employeeForm.email} onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })} /></Col>
                        <Col md={2}>
                            <Form.Select value={employeeForm.employment_type} onChange={(e) => setEmployeeForm({ ...employeeForm, employment_type: e.target.value })}>
                                <option value="FULL_TIME">Full-time</option>
                                <option value="CASUAL">Casual</option>
                                <option value="CONTRACT">Contract</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>{renderSelect(employeeForm.store, (value) => setEmployeeForm({ ...employeeForm, store: value }), stores)}</Col>
                        <Col md={2}>{renderSelect(employeeForm.department, (value) => setEmployeeForm({ ...employeeForm, department: value }), departments)}</Col>
                        <Col md={2}>{renderSelect(employeeForm.role, (value) => setEmployeeForm({ ...employeeForm, role: value }), roles, "title")}</Col>
                        <Col md={2}>{renderSelect(employeeForm.salary_structure, (value) => setEmployeeForm({ ...employeeForm, salary_structure: value }), salaryStructures)}</Col>
                        <Col md={2}><Form.Control type="date" value={employeeForm.hire_date} onChange={(e) => setEmployeeForm({ ...employeeForm, hire_date: e.target.value })} /></Col>
                        <Col md={2}>
                            <Button
                                disabled={saving}
                                onClick={() => submit(async () => {
                                    await createHREmployee({
                                        ...employeeForm,
                                        supervisor: employeeForm.supervisor || null,
                                    });
                                    setEmployeeForm(blankEmployee);
                                }, "Employee saved.")}
                            >
                                Save Employee
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Table responsive bordered hover>
                <thead>
                    <tr>
                        <th>Employee</th>
                        <th>Employment</th>
                        <th>Store</th>
                        <th>Department / Role</th>
                        <th>Salary Structure</th>
                        <th>Supervisor</th>
                        <th>Docs</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {employees.map((employee) => (
                        <tr key={employee.id}>
                            <td>
                                <strong>{employee.full_name}</strong>
                                <div className="text-muted small">{employee.employee_number} / ID {employee.id_number || "Missing"}</div>
                            </td>
                            <td>{employee.employment_type}</td>
                            <td>{employee.store_name}</td>
                            <td>{employee.department_name} / {employee.role_title}</td>
                            <td>{employee.salary_structure_name}</td>
                            <td>{employee.supervisor_name || "-"}</td>
                            <td>
                                <Badge bg={employee.kra_pin ? "success" : "danger"} className="me-1">KRA</Badge>
                                <Badge bg={employee.sha_number ? "success" : "danger"} className="me-1">SHA</Badge>
                                <Badge bg={employee.nssf_number ? "success" : "danger"}>NSSF</Badge>
                            </td>
                            <td><Badge bg={employee.status === "ACTIVE" ? "success" : "warning"}>{employee.status}</Badge></td>
                        </tr>
                    ))}
                    {!employees.length && <tr><td colSpan={8} className="text-center text-muted">No employees yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderAttendance = () => (
        <>
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Record Attendance</Card.Title>
                    <Row className="g-2">
                        <Col md={2}>{renderSelect(attendanceForm.employee, (value) => setAttendanceForm({ ...attendanceForm, employee: value }), employees, "full_name")}</Col>
                        <Col md={2}>{renderSelect(attendanceForm.store, (value) => setAttendanceForm({ ...attendanceForm, store: value }), stores)}</Col>
                        <Col md={2}><Form.Control type="date" value={attendanceForm.attendance_date} onChange={(e) => setAttendanceForm({ ...attendanceForm, attendance_date: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="Shift" value={attendanceForm.shift_name} onChange={(e) => setAttendanceForm({ ...attendanceForm, shift_name: e.target.value })} /></Col>
                        <Col md={1}><Form.Control type="number" placeholder="Late" value={attendanceForm.late_minutes} onChange={(e) => setAttendanceForm({ ...attendanceForm, late_minutes: e.target.value })} /></Col>
                        <Col md={1}><Form.Control type="number" step="0.01" placeholder="OT" value={attendanceForm.overtime_hours} onChange={(e) => setAttendanceForm({ ...attendanceForm, overtime_hours: e.target.value })} /></Col>
                        <Col md={1}><Form.Check label="Absent" checked={attendanceForm.is_absent} onChange={(e) => setAttendanceForm({ ...attendanceForm, is_absent: e.target.checked })} /></Col>
                        <Col md={1}><Button disabled={saving} onClick={() => submit(async () => createHRAttendance(attendanceForm), "Attendance saved.")}>Save</Button></Col>
                    </Row>
                </Card.Body>
            </Card>
            <Table responsive bordered hover>
                <thead><tr><th>Employee</th><th>Store</th><th>Date</th><th>Shift</th><th>Late</th><th>Overtime</th><th>Absent</th><th>Payroll</th></tr></thead>
                <tbody>
                    {attendance.map((record) => (
                        <tr key={record.id}>
                            <td>{record.employee_name}</td>
                            <td>{record.store_name}</td>
                            <td>{record.attendance_date}</td>
                            <td>{record.shift_name || "-"}</td>
                            <td><Badge bg={Number(record.late_minutes) > 10 ? "danger" : "success"}>{record.late_minutes} min</Badge></td>
                            <td>{record.overtime_hours} hrs</td>
                            <td>{record.is_absent ? "Yes" : "No"}</td>
                            <td>{record.payroll_processed ? "Processed" : "Open"}</td>
                        </tr>
                    ))}
                    {!attendance.length && <tr><td colSpan={8} className="text-center text-muted">No attendance records yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderLeave = () => (
        <>
            <Row className="g-3 mb-3">
                <Col lg={5}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Leave Type</Card.Title>
                            <Row className="g-2">
                                <Col md={5}><Form.Control placeholder="Name" value={leaveTypeForm.name} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, name: e.target.value })} /></Col>
                                <Col md={3}><Form.Control type="number" placeholder="Annual" value={leaveTypeForm.annual_entitlement_days} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, annual_entitlement_days: e.target.value })} /></Col>
                                <Col md={3}><Form.Control type="number" placeholder="Statutory" value={leaveTypeForm.statutory_minimum_days} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, statutory_minimum_days: e.target.value })} /></Col>
                                <Col md={1}><Button disabled={saving} onClick={() => submit(async () => createHRLeaveType(leaveTypeForm), "Leave type saved.")}>Save</Button></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={7}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Leave Request</Card.Title>
                            <Row className="g-2">
                                <Col md={3}>{renderSelect(leaveForm.employee, (value) => setLeaveForm({ ...leaveForm, employee: value }), employees, "full_name")}</Col>
                                <Col md={2}>{renderSelect(leaveForm.leave_type, (value) => setLeaveForm({ ...leaveForm, leave_type: value }), leaveTypes)}</Col>
                                <Col md={2}><Form.Control type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} /></Col>
                                <Col md={2}><Form.Control type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} /></Col>
                                <Col md={1}><Form.Control type="number" placeholder="Days" value={leaveForm.days_requested} onChange={(e) => setLeaveForm({ ...leaveForm, days_requested: e.target.value })} /></Col>
                                <Col md={2}><Button disabled={saving} onClick={() => submit(async () => createHRLeaveRequest(leaveForm), "Leave request saved.")}>Save Request</Button></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Table responsive bordered hover>
                <thead><tr><th>Employee</th><th>Store</th><th>Type</th><th>Dates</th><th>Days</th><th>Balance</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                    {leaveRequests.map((request) => (
                        <tr key={request.id}>
                            <td>{request.employee_name}</td>
                            <td>{request.store_name}</td>
                            <td>{request.leave_type_name}</td>
                            <td>{request.start_date} to {request.end_date}</td>
                            <td>{request.days_requested}</td>
                            <td>{request.balance_before} to {request.balance_after}</td>
                            <td><Badge bg={request.status === "APPROVED" ? "success" : request.status === "PENDING" ? "warning" : "danger"}>{request.status}</Badge></td>
                            <td>
                                {request.status === "PENDING" ? (
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="outline-success" disabled={saving} onClick={() => submit(async () => approveHRLeaveRequest(request.id), "Leave approved.")}>Approve</Button>
                                        <Button size="sm" variant="outline-danger" disabled={saving} onClick={() => submit(async () => rejectHRLeaveRequest(request.id), "Leave rejected.")}>Reject</Button>
                                    </div>
                                ) : "-"}
                            </td>
                        </tr>
                    ))}
                    {!leaveRequests.length && <tr><td colSpan={8} className="text-center text-muted">No leave requests yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderPayroll = () => (
        <>
            <Row className="g-3 mb-3">
                <Col lg={4}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Payroll Period</Card.Title>
                            <Row className="g-2">
                                <Col md={4}><Form.Control placeholder="Name" value={payrollPeriodForm.name} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, name: e.target.value })} /></Col>
                                <Col md={3}><Form.Control type="date" value={payrollPeriodForm.start_date} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, start_date: e.target.value })} /></Col>
                                <Col md={3}><Form.Control type="date" value={payrollPeriodForm.end_date} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, end_date: e.target.value })} /></Col>
                                <Col md={2}><Button disabled={saving} onClick={() => submit(async () => createHRPayrollPeriod(payrollPeriodForm), "Payroll period saved.")}>Save</Button></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={8}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Payroll Line</Card.Title>
                            <Row className="g-2">
                                <Col md={2}>{renderSelect(payrollItemForm.period, (value) => setPayrollItemForm({ ...payrollItemForm, period: value }), payrollPeriods)}</Col>
                                <Col md={2}>{renderSelect(payrollItemForm.employee, (value) => setPayrollItemForm({ ...payrollItemForm, employee: value }), employees, "full_name")}</Col>
                                <Col md={2}>{renderSelect(payrollItemForm.store, (value) => setPayrollItemForm({ ...payrollItemForm, store: value }), stores)}</Col>
                                <Col md={1}><Form.Control type="number" placeholder="Basic" value={payrollItemForm.basic_pay} onChange={(e) => setPayrollItemForm({ ...payrollItemForm, basic_pay: e.target.value })} /></Col>
                                <Col md={1}><Form.Control type="number" placeholder="Allow" value={payrollItemForm.allowances} onChange={(e) => setPayrollItemForm({ ...payrollItemForm, allowances: e.target.value })} /></Col>
                                <Col md={1}><Form.Control type="number" placeholder="PAYE" value={payrollItemForm.paye} onChange={(e) => setPayrollItemForm({ ...payrollItemForm, paye: e.target.value })} /></Col>
                                <Col md={1}><Form.Control type="number" placeholder="SHA" value={payrollItemForm.sha} onChange={(e) => setPayrollItemForm({ ...payrollItemForm, sha: e.target.value })} /></Col>
                                <Col md={1}><Form.Control type="number" placeholder="NSSF" value={payrollItemForm.nssf} onChange={(e) => setPayrollItemForm({ ...payrollItemForm, nssf: e.target.value })} /></Col>
                                <Col md={1}><Button disabled={saving} onClick={() => submit(async () => createHRPayrollItem(payrollItemForm), "Payroll line saved.")}>Save</Button></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Table responsive bordered hover>
                <thead><tr><th>Employee</th><th>Store</th><th>Period</th><th>Basic</th><th>Allowances</th><th>Overtime</th><th>PAYE</th><th>SHA</th><th>NSSF</th><th>Net Pay</th></tr></thead>
                <tbody>
                    {payrollItems.map((line) => (
                        <tr key={line.id}>
                            <td>{line.employee_name}</td>
                            <td>{line.store_name}</td>
                            <td>{line.period_name}</td>
                            <td>{currency(line.basic_pay)}</td>
                            <td>{currency(line.allowances)}</td>
                            <td>{currency(line.overtime_pay)}</td>
                            <td>{currency(line.paye)}</td>
                            <td>{currency(line.sha)}</td>
                            <td>{currency(line.nssf)}</td>
                            <td><strong>{currency(line.net_pay)}</strong></td>
                        </tr>
                    ))}
                    {!payrollItems.length && <tr><td colSpan={10} className="text-center text-muted">No payroll lines yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderPerformance = () => (
        <>
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Performance Review</Card.Title>
                    <Row className="g-2">
                        <Col md={2}>{renderSelect(performanceForm.employee, (value) => setPerformanceForm({ ...performanceForm, employee: value }), employees, "full_name")}</Col>
                        <Col md={2}><Form.Control type="date" value={performanceForm.period_start} onChange={(e) => setPerformanceForm({ ...performanceForm, period_start: e.target.value })} /></Col>
                        <Col md={2}><Form.Control type="date" value={performanceForm.period_end} onChange={(e) => setPerformanceForm({ ...performanceForm, period_end: e.target.value })} /></Col>
                        <Col md={1}><Form.Control type="number" placeholder="Score" value={performanceForm.attendance_score} onChange={(e) => setPerformanceForm({ ...performanceForm, attendance_score: e.target.value })} /></Col>
                        <Col md={1}><Form.Control type="number" placeholder="Late" value={performanceForm.late_days} onChange={(e) => setPerformanceForm({ ...performanceForm, late_days: e.target.value })} /></Col>
                        <Col md={1}><Form.Control type="number" placeholder="Stock" value={performanceForm.stock_variance_flags} onChange={(e) => setPerformanceForm({ ...performanceForm, stock_variance_flags: e.target.value })} /></Col>
                        <Col md={1}><Form.Control type="number" placeholder="Void" value={performanceForm.return_void_flags} onChange={(e) => setPerformanceForm({ ...performanceForm, return_void_flags: e.target.value })} /></Col>
                        <Col md={1}><Form.Control placeholder="Action" value={performanceForm.action} onChange={(e) => setPerformanceForm({ ...performanceForm, action: e.target.value })} /></Col>
                        <Col md={1}><Button disabled={saving} onClick={() => submit(async () => createHRPerformanceReview(performanceForm), "Performance review saved.")}>Save</Button></Col>
                    </Row>
                </Card.Body>
            </Card>
            <Table responsive bordered hover>
                <thead><tr><th>Employee</th><th>Store</th><th>Period</th><th>Attendance Score</th><th>Late Days</th><th>Stock Flags</th><th>Return/Void Flags</th><th>Action</th></tr></thead>
                <tbody>
                    {performanceReviews.map((review) => (
                        <tr key={review.id}>
                            <td>{review.employee_name}</td>
                            <td>{review.store_name}</td>
                            <td>{review.period_start} to {review.period_end}</td>
                            <td><ProgressBar now={percent(review.attendance_score)} label={`${review.attendance_score}%`} /></td>
                            <td>{review.late_days}</td>
                            <td>{review.stock_variance_flags}</td>
                            <td>{review.return_void_flags}</td>
                            <td>{review.action || "-"}</td>
                        </tr>
                    ))}
                    {!performanceReviews.length && <tr><td colSpan={8} className="text-center text-muted">No reviews yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderCompliance = () => (
        <>
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Compliance Document</Card.Title>
                    <Row className="g-2">
                        <Col md={2}>{renderSelect(complianceForm.employee, (value) => setComplianceForm({ ...complianceForm, employee: value }), employees, "full_name")}</Col>
                        <Col md={2}>
                            <Form.Select value={complianceForm.document_type} onChange={(e) => setComplianceForm({ ...complianceForm, document_type: e.target.value })}>
                                <option value="CONTRACT">Contract</option>
                                <option value="KRA_PIN">KRA PIN</option>
                                <option value="SHA">SHA</option>
                                <option value="NSSF">NSSF</option>
                                <option value="ID">National ID</option>
                                <option value="OTHER">Other</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}><Form.Control placeholder="Number" value={complianceForm.document_number} onChange={(e) => setComplianceForm({ ...complianceForm, document_number: e.target.value })} /></Col>
                        <Col md={2}><Form.Control type="date" value={complianceForm.expiry_date} onChange={(e) => setComplianceForm({ ...complianceForm, expiry_date: e.target.value })} /></Col>
                        <Col md={2}>
                            <Form.Select value={complianceForm.status} onChange={(e) => setComplianceForm({ ...complianceForm, status: e.target.value })}>
                                <option value="VALID">Valid</option>
                                <option value="MISSING">Missing</option>
                                <option value="EXPIRED">Expired</option>
                                <option value="EXPIRING_SOON">Expiring Soon</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}><Button disabled={saving} onClick={() => submit(async () => createHRComplianceDocument(complianceForm), "Compliance document saved.")}>Save</Button></Col>
                    </Row>
                </Card.Body>
            </Card>
            <Table responsive bordered hover>
                <thead><tr><th>Employee</th><th>Store</th><th>Document</th><th>Number</th><th>Expiry</th><th>Status</th></tr></thead>
                <tbody>
                    {complianceDocuments.map((doc) => (
                        <tr key={doc.id}>
                            <td>{doc.employee_name}</td>
                            <td>{doc.store_name}</td>
                            <td>{doc.document_type}</td>
                            <td>{doc.document_number || "-"}</td>
                            <td>{doc.expiry_date || "-"}</td>
                            <td><Badge bg={doc.status === "VALID" ? "success" : "danger"}>{doc.status}</Badge></td>
                        </tr>
                    ))}
                    {!complianceDocuments.length && <tr><td colSpan={6} className="text-center text-muted">No compliance documents yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderRecruitment = () => (
       <>
            <Row className="g-3 mb-3">
                <Col lg={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Job Opening</Card.Title>
                            <Row className="g-2">
                                <Col md={4}><Form.Control placeholder="Title" value={jobOpeningForm.title} onChange={(e) => setJobOpeningForm({ ...jobOpeningForm, title: e.target.value })} /></Col>
                                <Col md={3}>{renderSelect(jobOpeningForm.store, (value) => setJobOpeningForm({ ...jobOpeningForm, store: value }), stores)}</Col>
                                <Col md={3}>{renderSelect(jobOpeningForm.department, (value) => setJobOpeningForm({ ...jobOpeningForm, department: value }), departments)}</Col>
                                <Col md={2}><Button disabled={saving} onClick={() => submit(async () => createHRJobOpening({ ...jobOpeningForm, role: jobOpeningForm.role || null }), "Job opening saved.")}>Save</Button></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Applicant</Card.Title>
                            <Row className="g-2">
                                <Col md={3}>{renderSelect(applicantForm.job_opening, (value) => setApplicantForm({ ...applicantForm, job_opening: value }), jobOpenings, "title")}</Col>
                                <Col md={3}><Form.Control placeholder="Name" value={applicantForm.full_name} onChange={(e) => setApplicantForm({ ...applicantForm, full_name: e.target.value })} /></Col>
                                <Col md={2}><Form.Control placeholder="Phone" value={applicantForm.phone_number} onChange={(e) => setApplicantForm({ ...applicantForm, phone_number: e.target.value })} /></Col>
                                <Col md={2}>
                                    <Form.Select value={applicantForm.status} onChange={(e) => setApplicantForm({ ...applicantForm, status: e.target.value })}>
                                        <option value="APPLIED">Applied</option>
                                        <option value="SHORTLISTED">Shortlisted</option>
                                        <option value="INTERVIEW">Interview</option>
                                        <option value="HIRED">Hired</option>
                                        <option value="REJECTED">Rejected</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}><Button disabled={saving} onClick={() => submit(async () => createHRApplicant(applicantForm), "Applicant saved.")}>Save</Button></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Table responsive bordered hover>
                <thead><tr><th>Opening</th><th>Store</th><th>Department</th><th>Role</th><th>Status</th><th>Applicants</th></tr></thead>
                <tbody>
                    {jobOpenings.map((opening) => (
                        <tr key={opening.id}>
                            <td>{opening.title}</td>
                            <td>{opening.store_name}</td>
                            <td>{opening.department_name}</td>
                            <td>{opening.role_title || "-"}</td>
                            <td>{opening.status}</td>
                            <td>{opening.applicant_count || 0}</td>
                        </tr>
                    ))}
                    {!jobOpenings.length && <tr><td colSpan={6} className="text-center text-muted">No job openings yet.</td></tr>}
                </tbody>
            </Table>
            <Table responsive bordered hover>
                <thead><tr><th>Applicant</th><th>Opening</th><th>Store</th><th>Phone</th><th>Email</th><th>Status</th></tr></thead>
                <tbody>
                    {applicants.map((applicant) => (
                        <tr key={applicant.id}>
                            <td>{applicant.full_name}</td>
                            <td>{applicant.job_title}</td>
                            <td>{applicant.store_name}</td>
                            <td>{applicant.phone_number || "-"}</td>
                            <td>{applicant.email || "-"}</td>
                            <td>{applicant.status}</td>
                        </tr>
                    ))}
                    {!applicants.length && <tr><td colSpan={6} className="text-center text-muted">No applicants yet.</td></tr>}
                </tbody>
            </Table>
        </>
    );

    const renderAnalytics = () => {
        const payrollByStore = dashboard.payroll_by_store || [];
        const maxPayroll = Math.max(1, ...payrollByStore.map((row) => Number(row.total || 0)));
        return (
            <>
                <Row className="g-3 mb-3">
                    <Col md={3}><Card><Card.Body><Card.Title>Absentee Records</Card.Title><h2>{dashboard.absentee_records || 0}</h2></Card.Body></Card></Col>
                    <Col md={3}><Card><Card.Body><Card.Title>Overtime Cost</Card.Title><h2>{currency(dashboard.overtime_cost)}</h2></Card.Body></Card></Col>
                    <Col md={3}><Card><Card.Body><Card.Title>Compliance Gaps</Card.Title><h2>{dashboard.compliance_gaps || 0}</h2></Card.Body></Card></Col>
                    <Col md={3}><Card><Card.Body><Card.Title>Open Roles</Card.Title><h2>{dashboard.open_roles || 0}</h2></Card.Body></Card></Col>
                </Row>
                <Table responsive bordered hover>
                    <thead><tr><th>Store</th><th>Payroll Cost</th><th>Cost Share</th></tr></thead>
                    <tbody>
                        {payrollByStore.map((row) => (
                            <tr key={row.store_id || row.store_name}>
                                <td>{row.store_name}</td>
                                <td>{currency(row.total)}</td>
                                <td><ProgressBar now={(Number(row.total || 0) / maxPayroll) * 100} /></td>
                            </tr>
                        ))}
                        {!payrollByStore.length && <tr><td colSpan={3} className="text-center text-muted">No payroll by store yet.</td></tr>}
                    </tbody>
                </Table>
            </>
        );
    };

    const renderSettings = () => (
        <Row className="g-3">
            <Col lg={4}>
                <Card>
                    <Card.Body>
                        <Card.Title>Departments</Card.Title>
                        <div className="d-flex gap-2 mb-3">
                            <Form.Control placeholder="Department name" value={departmentName} onChange={(e) => setDepartmentName(e.target.value)} />
                            <Button disabled={saving} onClick={() => submit(async () => {
                                await createHRDepartment({ name: departmentName });
                                setDepartmentName("");
                            }, "Department saved.")}>Save</Button>
                        </div>
                        {departments.map((department) => <Badge key={department.id} bg="secondary" className="me-1 mb-1">{department.name}</Badge>)}
                    </Card.Body>
                </Card>
            </Col>
            <Col lg={4}>
                <Card>
                    <Card.Body>
                        <Card.Title>Roles</Card.Title>
                        <div className="d-grid gap-2">
                            {renderSelect(roleForm.department, (value) => setRoleForm({ ...roleForm, department: value }), departments)}
                            <Form.Control placeholder="Role title" value={roleForm.title} onChange={(e) => setRoleForm({ ...roleForm, title: e.target.value })} />
                            <Button disabled={saving} onClick={() => submit(async () => createHRRole(roleForm), "Role saved.")}>Save Role</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col lg={4}>
                <Card>
                    <Card.Body>
                        <Card.Title>Salary Structures</Card.Title>
                        <div className="d-grid gap-2">
                            <Form.Control placeholder="Structure name" value={salaryForm.name} onChange={(e) => setSalaryForm({ ...salaryForm, name: e.target.value })} />
                            <Form.Select value={salaryForm.employment_type} onChange={(e) => setSalaryForm({ ...salaryForm, employment_type: e.target.value })}>
                                <option value="FULL_TIME">Full-time</option>
                                <option value="CASUAL">Casual</option>
                                <option value="CONTRACT">Contract</option>
                            </Form.Select>
                            <Form.Control type="number" placeholder="Basic salary" value={salaryForm.basic_salary} onChange={(e) => setSalaryForm({ ...salaryForm, basic_salary: e.target.value })} />
                            <Button disabled={saving} onClick={() => submit(async () => createHRSalaryStructure(salaryForm), "Salary structure saved.")}>Save Structure</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    const renderActiveModule = () => {
        const renderers = {
            employees: renderEmployees,
            attendance: renderAttendance,
            leave: renderLeave,
            payroll: renderPayroll,
            performance: renderPerformance,
            compliance: renderCompliance,
            recruitment: renderRecruitment,
            analytics: renderAnalytics,
            settings: renderSettings,
        };
        return renderers[activeModule]();
    };

    return (
        <Container fluid className="mt-4 hr-page text-start">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1 className="mb-0">Human Resources</h1>
                </Col>
                <Col xs="auto">
                    <Button variant="outline-secondary" onClick={loadHR} disabled={loading || saving}>Refresh</Button>
                </Col>
            </Row>

            {message && <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}

            <Row className="g-3 mb-3">
                <Col md={3}><Card><Card.Body><Card.Title>Active Staff</Card.Title><h2>{dashboard.active_staff || 0}</h2></Card.Body></Card></Col>
                <Col md={3}><Card><Card.Body><Card.Title>Monthly Payroll</Card.Title><h2>{currency(dashboard.monthly_payroll)}</h2></Card.Body></Card></Col>
                <Col md={3}><Card><Card.Body><Card.Title>Compliance Gaps</Card.Title><h2>{dashboard.compliance_gaps || 0}</h2></Card.Body></Card></Col>
                <Col md={3}><Card><Card.Body><Card.Title>Pending Approvals</Card.Title><h2>{dashboard.pending_leave || 0}</h2></Card.Body></Card></Col>
            </Row>

            <Row className="g-3">
                <Col lg={3}>
                    <Card>
                        <Card.Body>
                            <Card.Title>HR Modules</Card.Title>
                            <div className="d-grid gap-2">
                                {modules.map((module) => (
                                    <Button
                                        key={module.key}
                                        variant={activeModule === module.key ? "dark" : "outline-dark"}
                                        className="text-start"
                                        onClick={() => setActiveModule(module.key)}
                                    >
                                        {module.label}
                                    </Button>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={9}>
                    <div className="hr-workspace-panel">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">{modules.find((module) => module.key === activeModule)?.label}</h4>
                            <Badge bg="secondary">Live API</Badge>
                        </div>
                        {loading ? <div className="text-center py-5"><Spinner animation="border" /></div> : renderActiveModule()}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default HR;